import React, { useEffect, useState, useRef } from 'react';
import { Upload, Globe, ChevronDown, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Language } from '../i18n/translations';
import { CVFormModal } from './CVFormModal';
const languageLabels: Record<Language, string> = {
  en: 'EN',
  ka: 'KA'
};
const languageNames: Record<Language, string> = {
  en: 'English',
  ka: 'ქართული'
};
export function Header() {
  const {
    language,
    setLanguage,
    t,
    getLink
  } = useLanguage();
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <>
      <header className="w-full bg-white py-4 px-6 md:px-12 sticky top-0 z-50 shadow-sm" data-id="element-479">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4" data-id="element-480">
          {/* Logo */}
          <a href={getLink('/')} className="flex items-center flex-shrink-0" data-id="element-481">
            <img src="https://cdn.magicpatterns.com/uploads/4nkahh2hNsVZTnNq46Gbps/elvora_logo_cutout.png" alt="ELVORA" className="h-12 w-auto" width={120} height={48} fetchPriority="high" style={{
            clipPath: 'inset(0 0 18% 0)'
          }} data-id="element-482" />
          </a>

          {/* Center Navigation - Desktop */}
          <nav className="hidden lg:flex items-center justify-center gap-3 flex-1" data-id="element-483">
            <a href={getLink('/companies')} className="px-5 py-2 text-sm font-medium text-white rounded-full bg-[var(--color-tan)] hover:opacity-90 transition-opacity" data-id="element-484">
              {t('header.forCompanies')}
            </a>
            <a href={getLink('/specialists')} className="px-5 py-2 text-sm font-medium text-white rounded-full bg-[var(--color-tan)] hover:opacity-90 transition-opacity" data-id="element-485">
              {t('header.forSeafarers')}
            </a>
            <a href={getLink('/offshore-ready')} className="px-5 py-2 text-sm font-medium text-white rounded-full bg-[var(--color-tan)] hover:opacity-90 transition-opacity" data-id="element-486">
              {t('header.offshoreServices')}
            </a>
          </nav>

          {/* Right side - Language + Upload */}
          <div className="flex items-center gap-3" data-id="element-487">
            {/* Language Dropdown - Desktop */}
            <div className="hidden md:block relative" ref={dropdownRef} data-id="element-488">
              <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full hover:border-gray-300 transition-colors" data-id="element-489">
                <Globe className="w-4 h-4 text-gray-500" data-id="element-490" />
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-491">
                  {languageLabels[language]}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-492" />
              </button>

              {languageDropdownOpen && <div className="absolute top-full right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-493">
                  {(Object.keys(languageNames) as Language[]).map(lang => <button key={lang} onClick={() => {
                setLanguage(lang);
                setLanguageDropdownOpen(false);
              }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${language === lang ? 'bg-[var(--color-tan)]/10 text-[var(--color-tan)] font-medium' : 'text-[var(--color-text-dark)]'}`} data-id="element-494">
                      {languageNames[lang]}
                    </button>)}
                </div>}
            </div>

            {/* Send CV Button - Desktop (opens modal) */}
            <button onClick={() => setCvModalOpen(true)} className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity" data-id="element-495">
              <Upload className="w-4 h-4" data-id="element-496" />
              <span className="hidden lg:inline" data-id="element-497">{t('header.sendCV')}</span>
            </button>

            {/* Mobile - Language + Send CV */}
            <div className="flex md:hidden items-center gap-2" ref={mobileDropdownRef} data-id="element-498">
              <div className="relative" data-id="element-499">
                <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="p-2.5 border border-gray-200 rounded-full hover:border-gray-300 transition-colors" data-id="element-500">
                  <Globe className="w-5 h-5 text-gray-500" data-id="element-501" />
                </button>

                {languageDropdownOpen && <div className="absolute top-full right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50" data-id="element-502">
                    {(Object.keys(languageNames) as Language[]).map(lang => <button key={lang} onClick={() => {
                  setLanguage(lang);
                  setLanguageDropdownOpen(false);
                }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${language === lang ? 'bg-[var(--color-tan)]/10 text-[var(--color-tan)] font-medium' : 'text-[var(--color-text-dark)]'}`} data-id="element-503">
                        {languageNames[lang]}
                      </button>)}
                  </div>}
              </div>
              {/* Mobile Send CV button (opens modal) */}
              <button onClick={() => setCvModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity" data-id="element-504">
                <Upload className="w-4 h-4" data-id="element-505" />
                <span data-id="element-506">{t('header.sendCV')}</span>
              </button>
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2" aria-label="Menu" data-id="element-507">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-508" /> : <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-509">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-510" />
                </svg>}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} data-id="element-511">
          <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl p-6" onClick={e => e.stopPropagation()} data-id="element-512">
            <div className="flex justify-between items-center mb-8" data-id="element-513">
              <h3 className="text-lg font-semibold text-[var(--color-text-dark)]" data-id="element-514">
                {t('header.menu')}
              </h3>
              <button onClick={() => setMobileMenuOpen(false)} data-id="element-515">
                <X className="w-6 h-6" data-id="element-516" />
              </button>
            </div>

            <nav className="space-y-4" data-id="element-517">
              <a href={getLink('/companies')} className="block px-4 py-2 text-sm font-medium text-[var(--color-text-dark)] hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-id="element-518">
                {t('header.forCompanies')}
              </a>
              <a href={getLink('/specialists')} className="block px-4 py-2 text-sm font-medium text-[var(--color-text-dark)] hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-id="element-519">
                {t('header.forSeafarers')}
              </a>
              <a href={getLink('/offshore-ready')} className="block px-4 py-2 text-sm font-medium text-[var(--color-text-dark)] hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-id="element-520">
                {t('header.offshoreServices')}
              </a>
            </nav>
          </div>
        </div>}

      <CVFormModal isOpen={cvModalOpen} onClose={() => setCvModalOpen(false)} mode="seafarer" data-id="element-521" />
    </>;
}